import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { api } from '../api/client';
import { useFlags } from '../stores/flags';

// ── Tipos ──────────────────────────────────────────────────────
type Horario = { manana?: [string, string]; tarde?: [string, string] } | [string, string][] | null;

type Comercio = {
  id: number;
  razon_social: string;
  nombre_fantasia: string | null;
  descripcion: string | null;
  direccion: string | null;
  latitud: string | null;
  longitud: string | null;
  telefono: string | null;
  whatsapp: string | null;
  logo_url: string | null;
  horarios: Record<string, Horario> | null;
  redes: { instagram?: string; facebook?: string; web?: string; tiktok?: string } | null;
  score_actual: number;
  rubro_nombre: string;
  rubro_icono: string;
  municipio_nombre: string;
};

type Producto = {
  id: number; nombre: string; descripcion: string | null;
  categoria_producto: string | null; precio_referencia: number | null; imagen_url: string | null;
};

type Oferta = {
  id: number; titulo: string; descripcion: string | null;
  precio_oferta: number; fecha_tope: string | null;
  alcance_ciudad: boolean; radio_alcance_km: number;
  imagen_url: string | null; producto_nombre: string | null; producto_imagen: string | null;
};

type Calificacion = {
  ciudadano_nombre: string; ciudadano_inicial: string | null;
  score_calculado: number; comentario: string | null;
};

type Resumen = {
  total: number; prom_atencion: number; prom_calidad: number;
  prom_cumplimiento: number; prom_frecuencia: number;
};

type DetalleData = {
  comercio: Comercio;
  productos: Producto[];
  ofertas: Oferta[];
  calificaciones: Calificacion[];
  resumen_calificaciones: Resumen | null;
};

// ── Helpers ────────────────────────────────────────────────────
const DIAS = [
  { key: 'lun', label: 'Lunes'     },
  { key: 'mar', label: 'Martes'    },
  { key: 'mie', label: 'Miércoles' },
  { key: 'jue', label: 'Jueves'    },
  { key: 'vie', label: 'Viernes'   },
  { key: 'sab', label: 'Sábado'    },
  { key: 'dom', label: 'Domingo'   },
];

function formatHorarios(h: Record<string, Horario> | null) {
  if (!h) return null;
  return DIAS.map((d) => {
    const dia = h[d.key];
    if (!dia) return { dia: d.label, texto: 'Cerrado', cerrado: true };
    if (Array.isArray(dia)) {
      const r = (dia as [string, string][])[0] ?? [];
      const t = r[0] && r[1] ? `${r[0]} – ${r[1]}` : 'Cerrado';
      return { dia: d.label, texto: t, cerrado: t === 'Cerrado' };
    }
    const obj = dia as { manana?: [string, string]; tarde?: [string, string] };
    const m = obj.manana?.[0] && obj.manana?.[1] ? `${obj.manana[0]}–${obj.manana[1]}` : null;
    const t = obj.tarde?.[0]  && obj.tarde?.[1]  ? `${obj.tarde[0]}–${obj.tarde[1]}`   : null;
    const txt = [m, t].filter(Boolean).join(' · ') || 'Cerrado';
    return { dia: d.label, texto: txt, cerrado: txt === 'Cerrado' };
  });
}

function tiempoHasta(fechaTope: string | null) {
  if (!fechaTope) return null;
  const diff = new Date(fechaTope.replace(' ', 'T')).getTime() - Date.now();
  if (isNaN(diff) || diff <= 0) return { texto: 'Vencida', urgente: true };
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return { texto: `Vence en ${mins} min`, urgente: true };
  const horas = Math.floor(mins / 60);
  if (horas < 24) return { texto: `Vence en ${horas} h`, urgente: true };
  const dias = Math.floor(horas / 24);
  if (dias < 7) return { texto: `Vence en ${dias} día${dias > 1 ? 's' : ''}`, urgente: dias <= 2 };
  const d = new Date(fechaTope.replace(' ', 'T'));
  return { texto: `Vence ${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`, urgente: false };
}

function fmtPrecio(n: number) {
  return `$ ${Number(n).toLocaleString('es-AR')}`;
}

function SeccionTitulo({ emoji, texto, count }: { emoji: string; texto: string; count?: number }) {
  return (
    <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#94a3b8' }}>
      {emoji} {texto}{count !== undefined ? ` (${count})` : ''}
    </p>
  );
}

// ── Componente principal ───────────────────────────────────────
export default function DetalleComercio() {
  const { id }     = useParams<{ id: string }>();
  const nav        = useNavigate();
  const flagsData  = useFlags((s) => s.flags);
  const flagsAt    = useFlags((s) => s.fetchedAt);
  const hasFlag    = (f: string) => !flagsAt || flagsData[f] === true;

  const [data,         setData]         = useState<DetalleData | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState<string | null>(null);
  const [verProductos, setVerProductos] = useState(false);

  useEffect(() => {
    if (!id) return;
    api<DetalleData>(`/comercios?action=detalle&id=${id}`)
      .then(setData)
      .catch((e: any) => setError(e?.message ?? 'No se pudo cargar'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <>
        <TopBar title="Comercio" back />
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
          <p className="text-sm text-gray-400">Cargando...</p>
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <TopBar title="Comercio" back />
        <div className="px-4 py-16 text-center">
          <p className="text-4xl mb-3">😕</p>
          <p className="text-sm text-gray-500">{error || 'Comercio no disponible'}</p>
          <button onClick={() => nav(-1)} className="mt-4 text-sm font-semibold text-blue-600">Volver</button>
        </div>
      </>
    );
  }

  const { comercio: c, productos, ofertas, calificaciones, resumen_calificaciones: resumen } = data;
  const nombre   = c.nombre_fantasia || c.razon_social;
  const horarios = formatHorarios(c.horarios);

  const llamar    = () => { if (c.telefono) window.open(`tel:${c.telefono.replace(/\s/g, '')}`); };
  const whatsapp  = () => {
    if (!c.whatsapp) return;
    const num = c.whatsapp.replace(/[^\d]/g, '');
    const ar  = num.startsWith('54') ? num : `54${num}`;
    window.open(`https://wa.me/${ar}`);
  };
  const comoLlegar = () => {
    if (!c.latitud || !c.longitud) return;
    window.open(`https://maps.google.com/?q=${c.latitud},${c.longitud}`);
  };

  const tieneRedes = c.redes && Object.values(c.redes).some(Boolean);

  return (
    <>
      <TopBar title={nombre} back />

      <main className="max-w-xl mx-auto pb-16">

        {/* ── Hero ──────────────────────────────────────────── */}
        <div
          className="w-full flex items-center justify-center"
          style={{ height: 200, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}
        >
          {c.logo_url ? (
            <img src={c.logo_url} alt={nombre} className="max-h-full max-w-full object-contain p-6" />
          ) : (
            <span className="text-8xl leading-none">{c.rubro_icono || '🏪'}</span>
          )}
        </div>

        {/* ── Encabezado ─────────────────────────────────────── */}
        <div className="px-5 pt-5 pb-4">
          <h1 className="font-extrabold text-2xl text-gray-900 leading-tight">{nombre}</h1>
          {c.nombre_fantasia && c.razon_social !== c.nombre_fantasia && (
            <p className="text-xs text-gray-400 mt-0.5">{c.razon_social}</p>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2.5">
            <span className="text-sm font-semibold text-blue-600">{c.rubro_icono} {c.rubro_nombre}</span>
            <span className="flex items-center gap-1 text-sm font-bold" style={{ color: '#d97706' }}>
              ⭐ <span>{c.score_actual.toFixed(1)}</span>
            </span>
            {resumen && resumen.total > 0 && (
              <span className="text-xs text-gray-400">{resumen.total} reseña{resumen.total !== 1 ? 's' : ''}</span>
            )}
          </div>

          {c.descripcion && (
            <p className="text-sm text-gray-600 mt-3 leading-relaxed">{c.descripcion}</p>
          )}
        </div>

        {/* ── Acciones ───────────────────────────────────────── */}
        <div className="px-5 pb-5 grid grid-cols-3 gap-3">
          {[
            { icon: '📞', label: 'Llamar',       onClick: llamar,     disabled: !c.telefono },
            { icon: '💬', label: 'WhatsApp',      onClick: whatsapp,   disabled: !c.whatsapp },
            { icon: '🗺️',  label: 'Cómo llegar',  onClick: comoLlegar, disabled: !c.latitud || !c.longitud },
          ].map((btn) => (
            <button
              key={btn.label}
              onClick={btn.onClick}
              disabled={btn.disabled}
              className="flex flex-col items-center justify-center py-4 rounded-2xl border font-semibold text-xs gap-1.5 transition-opacity disabled:opacity-35"
              style={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0', color: '#374151', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}
            >
              <span className="text-2xl leading-none">{btn.icon}</span>
              {btn.label}
            </button>
          ))}
        </div>

        {/* ── Ubicación ──────────────────────────────────────── */}
        {c.direccion && (
          <>
            <div style={{ height: 1, backgroundColor: '#f1f5f9' }} />
            <div className="px-5 py-5">
              <SeccionTitulo emoji="📍" texto="Ubicación" />
              <div
                className="flex items-start gap-3 rounded-2xl p-4"
                style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}
              >
                <span className="text-xl leading-none mt-0.5">🏠</span>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{c.direccion}</p>
                  {c.municipio_nombre && (
                    <p className="text-xs text-gray-400 mt-0.5">{c.municipio_nombre}</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Horarios ───────────────────────────────────────── */}
        {horarios && (
          <>
            <div style={{ height: 1, backgroundColor: '#f1f5f9' }} />
            <div className="px-5 py-5">
              <SeccionTitulo emoji="🕐" texto="Horarios" />
              <div className="rounded-2xl overflow-hidden border" style={{ borderColor: '#e2e8f0' }}>
                {horarios.map((h, i) => (
                  <div
                    key={h.dia}
                    className="flex justify-between items-center px-4 py-3"
                    style={{
                      backgroundColor: i % 2 === 0 ? '#f8fafc' : '#ffffff',
                      borderBottom: i < horarios.length - 1 ? '1px solid #f1f5f9' : 'none',
                    }}
                  >
                    <span className="text-sm font-semibold text-gray-700 w-24">{h.dia}</span>
                    <span className={`text-sm ${h.cerrado ? 'text-gray-400' : 'text-gray-800 font-medium'}`}>
                      {h.texto}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── Redes sociales ─────────────────────────────────── */}
        {tieneRedes && (
          <>
            <div style={{ height: 1, backgroundColor: '#f1f5f9' }} />
            <div className="px-5 py-5">
              <SeccionTitulo emoji="🌐" texto="Redes" />
              <div className="flex flex-wrap gap-2.5">
                {c.redes?.instagram && (
                  <a
                    href={c.redes.instagram.startsWith('http') ? c.redes.instagram : `https://instagram.com/${c.redes.instagram.replace('@', '')}`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold"
                    style={{ color: '#e1306c', borderColor: '#fbcfe8', backgroundColor: '#fff0f6' }}
                  >
                    <span>📷</span> Instagram
                  </a>
                )}
                {c.redes?.facebook && (
                  <a href={c.redes.facebook} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold"
                    style={{ color: '#1877f2', borderColor: '#bfdbfe', backgroundColor: '#eff6ff' }}
                  >
                    <span>🔵</span> Facebook
                  </a>
                )}
                {c.redes?.web && (
                  <a href={c.redes.web} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold"
                    style={{ color: '#0f766e', borderColor: '#99f6e4', backgroundColor: '#f0fdfa' }}
                  >
                    <span>🌍</span> Sitio web
                  </a>
                )}
                {c.redes?.tiktok && (
                  <a href={c.redes.tiktok} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold"
                    style={{ color: '#374151', borderColor: '#e5e7eb', backgroundColor: '#f9fafb' }}
                  >
                    <span>🎵</span> TikTok
                  </a>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── Ofertas ────────────────────────────────────────── */}
        {hasFlag('mobile_comercios_ofertas') && ofertas.length > 0 && (
          <>
            <div style={{ height: 1, backgroundColor: '#f1f5f9' }} />
            <div className="px-5 py-5">
              <SeccionTitulo emoji="🏷️" texto="Ofertas vigentes" count={ofertas.length} />
              <div className="space-y-3">
                {ofertas.slice(0, 5).map((o) => {
                  const vence = tiempoHasta(o.fecha_tope);
                  const img   = o.producto_imagen || o.imagen_url;
                  return (
                    <div
                      key={o.id}
                      className="flex rounded-2xl border overflow-hidden"
                      style={{ borderColor: '#e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}
                    >
                      <div
                        className="w-28 flex-shrink-0 flex items-center justify-center p-2"
                        style={{ backgroundColor: '#f1f5f9', minHeight: 100 }}
                      >
                        {img ? (
                          <img src={img} alt={o.titulo} className="w-full h-full object-contain" style={{ maxHeight: 96 }} />
                        ) : (
                          <span className="text-4xl opacity-40">🏷️</span>
                        )}
                      </div>
                      <div className="flex-1 p-3.5 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <span
                              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: '#eff6ff', color: '#3b82f6' }}
                            >
                              {o.alcance_ciudad ? '🏛️ Toda la ciudad' : `📍 ${o.radio_alcance_km} km`}
                            </span>
                            {vence && (
                              <span
                                className="text-[10px] font-semibold"
                                style={{ color: vence.urgente ? '#d97706' : '#94a3b8' }}
                              >
                                ⏰ {vence.texto}
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-bold text-gray-900 leading-snug line-clamp-2">{o.titulo}</p>
                          {o.descripcion && (
                            <p className="text-xs text-gray-400 mt-1 line-clamp-2">{o.descripcion}</p>
                          )}
                        </div>
                        <p className="text-lg font-extrabold mt-2" style={{ color: '#059669' }}>
                          {fmtPrecio(o.precio_oferta)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* ── Catálogo de productos ──────────────────────────── */}
        {hasFlag('mobile_perfil_comercio_completo') && productos.length > 0 && (
          <>
            <div style={{ height: 1, backgroundColor: '#f1f5f9' }} />
            <div className="px-5 py-5">
              <SeccionTitulo emoji="📦" texto="Catálogo" count={productos.length} />
              {!verProductos ? (
                <button
                  onClick={() => setVerProductos(true)}
                  className="w-full flex items-center gap-3 rounded-2xl border px-5 py-4 text-left"
                  style={{ backgroundColor: '#f8fafc', borderColor: '#e2e8f0' }}
                >
                  <span className="text-2xl leading-none">📦</span>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-900">
                      Ver los {productos.length} productos del catálogo
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Precios de referencia actualizados por el comercio
                    </p>
                  </div>
                  <span className="text-blue-500 font-bold text-lg">↓</span>
                </button>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    {productos.map((p) => (
                      <div
                        key={p.id}
                        className="rounded-2xl border overflow-hidden"
                        style={{ borderColor: '#e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}
                      >
                        <div
                          className="aspect-square flex items-center justify-center p-3"
                          style={{ backgroundColor: '#f8fafc' }}
                        >
                          {p.imagen_url ? (
                            <img src={p.imagen_url} alt={p.nombre} className="w-full h-full object-contain" />
                          ) : (
                            <span className="text-4xl opacity-25">🍽️</span>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="text-xs font-semibold text-gray-800 leading-snug line-clamp-2">{p.nombre}</p>
                          {p.precio_referencia != null && (
                            <p className="text-base font-extrabold mt-1.5" style={{ color: '#2563eb' }}>
                              {fmtPrecio(p.precio_referencia)}
                            </p>
                          )}
                          {p.categoria_producto && (
                            <p className="text-[10px] font-semibold uppercase tracking-wide mt-1" style={{ color: '#94a3b8' }}>
                              {p.categoria_producto}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setVerProductos(false)}
                    className="mt-4 w-full text-center text-sm font-semibold py-2.5 rounded-xl border"
                    style={{ color: '#64748b', borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }}
                  >
                    ↑ Ocultar catálogo
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {/* ── Calificaciones ─────────────────────────────────── */}
        {hasFlag('mobile_comercios_calificar') && resumen && resumen.total > 0 && (
          <>
            <div style={{ height: 1, backgroundColor: '#f1f5f9' }} />
            <div className="px-5 py-5">
              <SeccionTitulo emoji="⭐" texto="Calificaciones" count={resumen.total} />

              {/* Resumen 4 dimensiones */}
              <div className="grid grid-cols-4 gap-2 mb-5">
                {[
                  { label: 'Atención',   val: resumen.prom_atencion     },
                  { label: 'Calidad',    val: resumen.prom_calidad      },
                  { label: 'Cumpl.',     val: resumen.prom_cumplimiento },
                  { label: 'Frecuencia', val: resumen.prom_frecuencia   },
                ].map(({ label, val }) => (
                  <div
                    key={label}
                    className="flex flex-col items-center rounded-2xl border py-3"
                    style={{ borderColor: '#e2e8f0', backgroundColor: '#fafafa' }}
                  >
                    <span className="text-[10px] text-gray-400 mb-1 text-center leading-tight">{label}</span>
                    <span className="text-lg font-extrabold" style={{ color: '#d97706' }}>
                      {Number(val).toFixed(1)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Reseñas */}
              <div className="space-y-2.5">
                {calificaciones.slice(0, 5).map((cal, i) => (
                  <div
                    key={i}
                    className="rounded-2xl border p-4"
                    style={{ borderColor: '#e2e8f0', backgroundColor: '#fafafa' }}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-bold text-gray-800">
                        {cal.ciudadano_nombre}{cal.ciudadano_inicial ? ` ${cal.ciudadano_inicial}.` : ''}
                      </span>
                      <span className="text-sm font-bold" style={{ color: '#d97706' }}>
                        ⭐ {Number(cal.score_calculado).toFixed(1)}
                      </span>
                    </div>
                    {cal.comentario && (
                      <p className="text-sm text-gray-500 mt-2 italic leading-relaxed">
                        "{cal.comentario}"
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

      </main>
    </>
  );
}
